import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { TranslationContext } from "../contexts/TranslationContext";
import { normalizeLang } from "../translations/registerStaticTranslations";
import {
  loadFaqTranslations,
  faqTranslationsRegistered,
} from "../translations/loadFaqTranslations";
import useFaqT from "../components/faq/useFaqT";
import FaqSearch from "../components/faq/FaqSearch";
import FaqTabs from "../components/faq/FaqTabs";
import FaqRoleBar from "../components/faq/FaqRoleBar";
import FaqAccordionRow from "../components/faq/FaqAccordionRow";
import FaqContactCard from "../components/faq/FaqContactCard";
import FaqDevRolePanel from "../components/faq/FaqDevRolePanel";
import { resolveFaqAudience } from "../utils/faqRoles";
import { loadFaqQuestions } from "../constants/faq/faqBranchData";
import { questionMatchesQuery } from "../utils/faqSearch";
import { FAQ_CATEGORIES, FAQ_COPY, categoryLabelFor } from "../constants/faq/faqStructure";
import { FAQ_SHOW_KEYS, FAQ_SUBGROUP_THRESHOLD } from "../constants/faq/faqConfig";

// /faq — FAQ page per design "TP FAQ Page.dc.html" F1–F8. Route + guard (bare
// PrivateRoute, login + KYC) are unchanged; the platform header/layout wraps
// this page.
//
// Mechanics: live search across the viewer's own tiers with highlight + empty
// state (F1), category tabs from data (F2), the "Viewing as" role bar from the
// REAL auth role state (F3), accordion with stable anchors `/faq#<question-key>`
// incl. auto-open deep links (F4), answer block with media slot, marker overlay
// and Desktop|Mobile toggle (F5), one contact card used twice (F6). Styling:
// square everything (0 radius except circles), accent #1F5EFF, Helvetica Neue +
// IBM Plex Mono, content capped at 1440.
//
// THE ROLE SPLIT IS A CONFIDENTIALITY BOUNDARY (Marin, 2026-08-10)
// ---------------------------------------------------------------
// "Hab direkt einen fehler gesehen, für andere rollen darfst du nicht anzeigen,
// der Agent und Agency darf sicher nicht wissen was alles die Clubs haben!"
// This replaces the earlier design rule (F3 / brief §5: "filtered, never
// hidden"). Consequences enforced on this page:
//   * no "Show all questions" toggle and no foreign-role pills — the role bar is
//     a read-only statement of who you are;
//   * search runs over the viewer's own questions only, so no title, snippet or
//     result count can betray a foreign branch;
//   * a deep link to a foreign question resolves to nothing — the same dead end
//     as a typo'd anchor, so the page is not an existence oracle either;
//   * the questions of foreign branches are never DOWNLOADED (the dataset and
//     its translations arrive per branch from constants/faq/faqBranchData.js and
//     translations/loadFaqTranslations.js). A filter that only decided what to
//     render would have been undone by the network tab.
const FAQ = () => {
  const { roles, user } = useSelector((state) => state.auth);
  const location = useLocation();
  // 767.98, not 768: Tailwind's `md` starts AT 768, so "(max-width: 768px)"
  // made both true at exactly 768 — the desktop frame rendered with the
  // portrait mobile image set (control pass, measured at 767/768/769).
  const isMobileViewport = useMediaQuery("(max-width: 767.98px)");
  const tr = useFaqT();

  // The dev role override is a DEVELOPMENT-ONLY escape hatch for exercising the
  // eight matrix types without eight accounts. FaqDevRolePanel renders nothing
  // outside development, and this second gate makes sure a production build
  // cannot reach a foreign branch even if the state were set some other way.
  const [devOverrideState, setDevOverride] = useState(null);
  const devOverride = process.env.NODE_ENV === "development" ? devOverrideState : null;

  const audience = useMemo(
    () => resolveFaqAudience({ roles, user }, devOverride),
    [roles, user, devOverride]
  );
  // Stable identity for effect dependencies (the Set is rebuilt on every
  // resolve, the branch list behind it rarely changes).
  const branchKey = useMemo(() => [...audience.branches].sort().join(","), [audience]);

  // The questions themselves. `null` until the viewer's branch chunks land —
  // there is deliberately no "all questions" import to fall back to.
  const [questions, setQuestions] = useState(null);
  useEffect(() => {
    let alive = true;
    setQuestions(null);
    loadFaqQuestions(audience.branches).then((loaded) => {
      if (alive) setQuestions(loaded);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchKey]);

  // The FAQ ANSWER texts are fetched on demand, per branch, instead of riding in
  // the main chunk — see translations/loadFaqTranslations.js. The page chrome,
  // the category and group labels are NOT part of that: they stay statically
  // registered and are translated from the first paint.
  //
  // English readers need nothing at all, because the English source text IS the
  // key; every other language would read the English source until the chunk
  // lands, so the question list waits for it behind the same skeleton the
  // dataset already uses — the wait of a lazy route, instead of a visible
  // English flash that rewrites itself.
  const translation = useContext(TranslationContext);
  const needsFaqBundles =
    normalizeLang((translation && translation.currentLanguage) || "en") !== "en";
  const [faqBundlesReady, setFaqBundlesReady] = useState(() =>
    faqTranslationsRegistered(audience.branches)
  );

  useEffect(() => {
    if (faqBundlesReady) return undefined;
    let alive = true;
    // loadFaqTranslations resolves (never rejects) — a chunk that cannot be
    // fetched un-gates the page with its English source text rather than
    // leaving the reader in front of a skeleton forever.
    loadFaqTranslations(audience.branches).then(() => {
      if (alive) setFaqBundlesReady(true);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faqBundlesReady, branchKey]);

  const awaitingContent = questions === null || (needsFaqBundles && !faqBundlesReady);

  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(FAQ_CATEGORIES[0].id);
  const [open, setOpen] = useState({});
  const [imgSet, setImgSet] = useState({});
  const [anchorFlash, setAnchorFlash] = useState(null);
  const [anchorMissing, setAnchorMissing] = useState(false);

  const hasQuery = query.trim().length > 0;
  const rows = useMemo(() => questions || [], [questions]);

  // Counts come from the viewer's own set, so a category can be empty for this
  // viewer (a clubless coach has four, a lawyer four, a basis-only account
  // five). Such a tab would open a blank area with no explanation — it is left
  // out instead.
  const tabs = useMemo(
    () =>
      FAQ_CATEGORIES.map((c) => ({
        id: c.id,
        // Perspective label: "Trusted Agent" is the club-side name; agent-only
        // viewers see the surface's agent-side title (faqStructure.js).
        label: categoryLabelFor(c, audience.branches),
        count: rows.filter((x) => x.category === c.id).length,
      })).filter((t) => t.count > 0),
    [rows, audience]
  );

  // Guard the selection against a tab this viewer does not have.
  const activeCatId =
    tabs.some((t) => t.id === activeCat) || tabs.length === 0 ? activeCat : tabs[0].id;

  // Sections: while searching — every category with hits, labelled (F4 section
  // labels); otherwise the active category alone. Both read `rows`, which only
  // ever holds the viewer's own questions.
  const sections = useMemo(() => {
    if (hasQuery) {
      const out = [];
      FAQ_CATEGORIES.forEach((c) => {
        const hits = rows.filter(
          (x) => x.category === c.id && questionMatchesQuery(tr(x.q), query)
        );
        if (hits.length)
          out.push({
            id: c.id,
            label: categoryLabelFor(c, audience.branches),
            showLabel: true,
            rows: hits,
          });
      });
      return out;
    }
    return [
      {
        id: activeCatId,
        label: "",
        showLabel: false,
        rows: rows.filter((x) => x.category === activeCatId),
      },
    ];
  }, [hasQuery, query, activeCatId, rows, tr, audience]);

  const resultCount = sections.reduce((a, s) => a + s.rows.length, 0);
  const isEmpty = hasQuery && resultCount === 0;

  // Anchor deep links `/faq#<question-key>`: scroll-to + auto-open + brief
  // highlight — for a question this viewer owns. A hash that names a question of
  // a FOREIGN branch is treated exactly like a hash that names nothing at all:
  // the page says it could not find it and shows no content. It used to flip
  // "Show all" on and open the foreign row, which is the loophole this change
  // closes. Nothing here can distinguish the two cases, on purpose.
  const questionsRef = useRef(questions);
  questionsRef.current = questions;
  useEffect(() => {
    const hash = decodeURIComponent(location.hash || "").replace(/^#/, "");
    // A hash that matches nothing (or was cleared) has to end a running flash
    // too: the cleanup below already cancelled the 2.2s timer, so bailing out
    // silently left the previous target ringed forever.
    if (!hash) {
      setAnchorFlash(null);
      setAnchorMissing(false);
      return undefined;
    }
    // Still loading — decide once the viewer's own set is here.
    if (!questionsRef.current) return undefined;

    const target = questionsRef.current.find((q) => q.id === hash);
    if (!target) {
      setAnchorFlash(null);
      setAnchorMissing(true);
      return undefined;
    }

    setAnchorMissing(false);
    setQuery("");
    setActiveCat(target.category);
    setOpen((o) => ({ ...o, [target.id]: true }));
    setAnchorFlash(target.id);

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(target.id);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "start", behavior: reduced ? "auto" : "smooth" });
      }
    }, 80);
    const flashTimer = setTimeout(() => setAnchorFlash(null), 2200);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(flashTimer);
    };
  }, [location.hash, questions]);

  // Tab click selects the category AND clears the query (F2); during an
  // active search no tab is active.
  const selectTab = useCallback((id) => {
    setActiveCat(id);
    setQuery("");
    setAnchorMissing(false);
  }, []);

  const toggleRow = useCallback((id) => {
    // Accordion: multiple rows open at once (F4).
    setOpen((o) => ({ ...o, [id]: !o[id] }));
  }, []);

  // Desktop|Mobile toggle: pre-selected by viewport, remembered per question.
  const setFor = useCallback(
    (q) => {
      if (imgSet[q.id]) return imgSet[q.id];
      return isMobileViewport && q.media && q.media.mobile ? "mobile" : "desktop";
    },
    [imgSet, isMobileViewport]
  );

  const renderRow = (q) => (
    <FaqAccordionRow
      key={q.id}
      question={q}
      query={query}
      open={Boolean(open[q.id])}
      onToggle={() => toggleRow(q.id)}
      imgSet={setFor(q)}
      onSetImgSet={(set) => setImgSet((s) => ({ ...s, [q.id]: set }))}
      flash={anchorFlash === q.id}
    />
  );

  // Subgroup headers inside a category from ≥5 visible questions (F4).
  const renderSectionRows = (sec) => {
    if (sec.showLabel || sec.rows.length < FAQ_SUBGROUP_THRESHOLD) {
      return sec.rows.map(renderRow);
    }
    const out = [];
    let lastGroup = null;
    sec.rows.forEach((q) => {
      if (q.group && q.group.key !== lastGroup) {
        lastGroup = q.group.key;
        out.push(
          <div key={`grp-${q.group.key}`} className="flex items-baseline gap-2.5 pb-2 pt-6">
            <span className="text-[13px] font-semibold text-[#14171C]">{tr(q.group.label)}</span>
            {FAQ_SHOW_KEYS && (
              <span className="font-faqmono text-[10px] text-[#B4B9C2]">{q.group.key}</span>
            )}
          </div>
        );
      }
      out.push(renderRow(q));
    });
    return out;
  };

  return (
    <div className="w-full bg-[#EDEEF0] font-faq text-[#14171C]">
      {/* Extra bottom padding below md keeps the closing contact CTA clear of
          the platform's floating support button (visual gate finding at 360). */}
      <div className="mx-auto w-full max-w-[1440px] px-4 pb-24 pt-6 md:px-8 md:py-10">
        <div className="mb-3 empty:mb-0">
          <FaqDevRolePanel override={devOverride} onOverride={setDevOverride} />
        </div>

        <div className="border border-[#DDE0E5] bg-white shadow-[0_1px_2px_rgba(20,23,28,.05)]">
          {/* F1 — header + search */}
          <div className="border-b border-[#EDEEF0] px-4 pb-6 pt-6 md:px-10 md:pb-10 md:pt-14 xl:px-[72px]">
            {/* Mobile (<md) follows F7's compact header: title + search only —
                kicker and intro are part of the desktop frame (F1). */}
            <div className="flex max-w-[720px] flex-col gap-3 md:gap-3.5">
              <div className="hidden font-faqmono text-[11px] uppercase tracking-[.14em] text-[#8A919D] md:block">
                {tr(FAQ_COPY.kicker)}
              </div>
              <h1 className="m-0 text-[26px] font-semibold leading-[1.15] tracking-[-.02em] md:text-[40px] md:leading-[1.1] md:tracking-[-.025em]">
                {tr(FAQ_COPY.title)}
              </h1>
              <div className="hidden text-[14px] leading-[1.55] text-[#5A626E] md:block md:text-[15px]">
                {tr(FAQ_COPY.intro)}
              </div>
            </div>
            <FaqSearch
              query={query}
              onQuery={setQuery}
              onClear={() => setQuery("")}
              resultCount={resultCount}
              categoryCount={sections.filter((s) => s.rows.length > 0).length}
            />
          </div>

          {/* F2 — category tabs */}
          <FaqTabs tabs={tabs} activeId={hasQuery ? null : activeCatId} onSelect={selectTab} />

          {/* F3 — role bar (read-only; the branch cannot be changed here) */}
          <FaqRoleBar roleLabel={audience.label} />

          {/* F4/F5 — accordion + answer blocks */}
          <div className="px-4 pb-8 pt-2 md:px-10 md:pb-10 xl:px-[72px]">
            {/* Waiting for the viewer's branch chunks (dataset and, for a
                non-English reader, the answer translations): rows in the
                accordion's own rhythm (56px, hairline separator) so nothing
                jumps when the real questions replace them. Widths are
                percentages of the column, so this stacks and never overflows
                on a 375px viewport. `data-no-translate` keeps the DOM
                translator off an element that carries no text. */}
            {awaitingContent && (
              <div aria-busy="true" data-no-translate="true" className="flex flex-col">
                {[62, 48, 71, 55, 44, 66, 58].map((width, i) => (
                  <div
                    key={width}
                    className="flex min-h-[56px] items-center border-b border-[#EDEEF0]"
                  >
                    <span
                      className="h-3.5 animate-pulse bg-[#EDEEF0]"
                      style={{ width: `${width}%`, animationDelay: `${i * 90}ms` }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* A deep link that does not resolve for this viewer — a foreign
                branch or simply a stale/typo'd key. One wording for both. */}
            {!awaitingContent && anchorMissing && !hasQuery && (
              <div className="mt-6 border border-[#DDE0E5] bg-[#FAFBFC] px-4 py-3 text-[14px] text-[#5A626E]">
                {tr("We couldn't find that question.")}
              </div>
            )}

            {!awaitingContent &&
              sections.map((sec) => (
                <div key={sec.id} className="flex flex-col">
                  {sec.showLabel && (
                    <div className="mb-1.5 mt-6 flex items-center gap-2.5">
                      <span className="text-[12px] font-semibold uppercase tracking-[.1em] text-[#14171C]">
                        {tr(sec.label)}
                      </span>
                      <span aria-hidden="true" className="h-px flex-1 bg-[#EDEEF0]" />
                    </div>
                  )}
                  {renderSectionRows(sec)}
                </div>
              ))}

            {/* F6 — search empty state → contact fallback */}
            {!awaitingContent && isEmpty && (
              <div className="mt-8 flex flex-col items-start gap-4">
                <div className="text-[18px] font-semibold">
                  {tr("Nothing found for “{{query}}”", { query: query.trim() })}
                </div>
                <div className="text-[14px] text-[#5A626E]">
                  {tr("Try a different word, or clear the search to browse the categories.")}
                </div>
                <FaqContactCard variant="empty" />
              </div>
            )}
          </div>

          {/* F6 — closing contact band */}
          <div className="border-t border-[#EDEEF0] bg-[#FAFBFC] px-4 py-6 md:px-10 md:pb-14 md:pt-9 xl:px-[72px]">
            <FaqContactCard variant="closing" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
